import { FC, useState } from 'react';
import { keyframes } from '@emotion/react';
import { styled } from '@mui/material/styles';
import IconRefresh from 'src/assets/images/icons/icon-refresh.svg';
import { ILandingSectionProps } from './types';
import { LandingLoader } from './Loader';
import { LandingLoaderCards } from './LoaderCards';

// Define rotation animation
const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// Styled refresh icon with animation
const RefreshIcon = styled('img')<{ refreshing?: boolean }>(({ refreshing }) => ({
  ...(refreshing && {
    animation: `${rotate} 2s linear infinite`,
  }),
}));

const SectionHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  paddingBottom: '10px',
  borderBottom: '1px solid #E6E8EC',
  marginBottom: '24px',
});

export const LandingSection: FC<ILandingSectionProps> = (props) => {
  const { icon, title, children, topRight, isLoading, skeletonType, onRefresh, id } = props;
  const [refreshing, setRefreshing] = useState<boolean>(false);

  const handleRefresh = () => {
    if (refreshing || !onRefresh) return;

    setRefreshing(true);
    onRefresh().finally(() => {
      setTimeout(() => setRefreshing(false), 500);
    });
  };

  return (
    <div className='relative mb-48'>
      <SectionHeader>
        <div className='flex-1'>
          <div className='flex-1 flex items-center'>
            <div className='flex items-center gap-6 xl:gap-8'>
              <img src={icon} alt='' className='w-20 xl:w-24' />
              <strong id={id} className='text-16 xl:text-20 font-bold text-blue_grey-30'>
                {title}
              </strong>
            </div>
            <div className='pl-10 ml-auto'>{topRight}</div>
          </div>
        </div>
        {onRefresh && (
          <div className='ml-auto pl-12 xl:pl-20'>
            <button
              type='button'
              onClick={handleRefresh}
              aria-label='Refresh content'
              className='w-36 xl:w-40 h-36 xl:h-40 rounded-6 border border-[#D0D5DD] flex items-center justify-center'
            >
              <RefreshIcon src={IconRefresh} alt='Refresh icon' className='w-18' refreshing={refreshing} />
            </button>
          </div>
        )}
      </SectionHeader>

      <div>{isLoading ? <>{skeletonType === 'cards' ? <LandingLoaderCards /> : <LandingLoader />}</> : children}</div>
    </div>
  );
};
